import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileCheck, 
  Upload, 
  Filter,
  Search,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { PortalLayout } from './PortalLayout';
import { CredentialUpload } from './CredentialUpload';
import { CredentialsList } from './CredentialsList';
import { useExchangeAuth, exchangeApi } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

export const CredentialsPage = ({ role = 'driver' }) => {
  const { user } = useExchangeAuth();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setLoading(true);
    try {
      const res = await exchangeApi.getCredentials();
      setCredentials(res.data.credentials || []);
    } catch (error) {
      console.error('Failed to load credentials:', error);
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (result) => {
    toast.success('Credential uploaded successfully!');
    loadCredentials();
    setShowUpload(false);
  };

  // Filter credentials
  const filteredCredentials = credentials.filter(cred => {
    // Status filter
    if (filter !== 'all') {
      if (filter === 'expiring' && !['expiring_soon', 'expired'].includes(cred.status)) {
        return false;
      }
      if (filter === 'verified' && cred.status !== 'verified') {
        return false;
      }
      if (filter === 'pending' && cred.status !== 'pending') {
        return false;
      }
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        cred.name?.toLowerCase().includes(query) ||
        cred.credential_type?.toLowerCase().includes(query) ||
        cred.description?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  // Stats
  const stats = {
    total: credentials.length,
    verified: credentials.filter(c => c.status === 'verified').length,
    pending: credentials.filter(c => c.status === 'pending').length,
    expiring: credentials.filter(c => ['expiring_soon', 'expired'].includes(c.status)).length
  };

  return (
    <PortalLayout role={role}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">My Credentials</h1>
            <p className="text-slate-400">Upload, manage, and share your credentials securely</p>
          </div>
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="btn-primary"
            data-testid="toggle-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {showUpload ? 'Cancel Upload' : 'Upload Credential'}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setFilter('all')}
            className={`glass-card p-4 text-left transition-all ${
              filter === 'all' ? 'border-neon-green' : 'hover:border-slate-600'
            }`}
          >
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-sm text-slate-400">Total Credentials</p>
          </button>
          <button
            onClick={() => setFilter('verified')}
            className={`glass-card p-4 text-left transition-all ${
              filter === 'verified' ? 'border-neon-green' : 'hover:border-slate-600'
            }`}
          >
            <p className="text-2xl font-bold text-neon-green">{stats.verified}</p>
            <p className="text-sm text-slate-400">Verified</p>
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`glass-card p-4 text-left transition-all ${
              filter === 'pending' ? 'border-neon-green' : 'hover:border-slate-600'
            }`}
          >
            <p className="text-2xl font-bold text-blue-400">{stats.pending}</p>
            <p className="text-sm text-slate-400">Pending Review</p>
          </button>
          <button
            onClick={() => setFilter('expiring')}
            className={`glass-card p-4 text-left transition-all ${
              filter === 'expiring' ? 'border-neon-green' : 'hover:border-slate-600'
            }`}
          >
            <p className={`text-2xl font-bold ${stats.expiring > 0 ? 'text-yellow-400' : 'text-slate-400'}`}>
              {stats.expiring}
            </p>
            <p className="text-sm text-slate-400">Expiring/Expired</p>
          </button>
        </div>

        {/* Upload Section */}
        {showUpload && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <CredentialUpload 
              role={role} 
              onSuccess={handleUploadSuccess}
              onClose={() => setShowUpload(false)}
            />
          </motion.div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search credentials..."
              className="w-full input-dark pl-10"
              data-testid="credential-search"
            />
          </div>
          <button
            onClick={loadCredentials}
            disabled={loading}
            className="btn-secondary"
            data-testid="refresh-credentials"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filter Pills */}
        {filter !== 'all' && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-400">Filtered by:</span>
            <span className="px-3 py-1 bg-neon-green/20 text-neon-green text-sm rounded-full flex items-center">
              {filter === 'verified' && 'Verified'}
              {filter === 'pending' && 'Pending Review'}
              {filter === 'expiring' && 'Expiring/Expired'}
              <button
                onClick={() => setFilter('all')}
                className="ml-2 hover:text-white"
              >
                ×
              </button>
            </span>
          </div>
        )}

        {/* Credentials List */}
        <div className="glass-card p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-neon-green animate-spin" />
            </div>
          ) : (
            <CredentialsList 
              credentials={filteredCredentials} 
              onRefresh={loadCredentials}
              role={role}
            />
          )}
        </div>
      </div>
    </PortalLayout>
  );
};

// Role-specific pages
export const DriverCredentialsPage = () => <CredentialsPage role="driver" />;
export const ProviderCredentialsPage = () => <CredentialsPage role="provider" />;
export const BrokerCredentialsPage = () => <CredentialsPage role="broker" />;

export default CredentialsPage;
